import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogProps,
    DialogTitle,
    styled
} from '@mui/material';

import { LoadingButton } from 'ui/LoadingButton';
import { isString } from './typeInference';

interface ConfirmDialogProps extends DialogProps {
    title?: string;
    onClose: () => void;
    description: string | JSX.Element;
    actionButtons?: React.ReactNode;
    isLoading?: boolean;
    confirmMessage?: string;
    cancelMessage?: string;
    isCancellable?: boolean;
    onConfirm: () => void;
    disableBackdropClick?: boolean;
    align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
}

const PREFIX = 'ConfirmDialog';

const classes = {
    dialogWrapper: `${PREFIX}dialogWrapper`
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
    [`& .${classes.dialogWrapper}`]: {
        padding: theme.spacing(2)
    }
}));

export const ConfirmDialog: React.FC<React.PropsWithChildren<ConfirmDialogProps>> = ({
    actionButtons = null,
    title,
    description,
    confirmMessage = 'Confirm',
    cancelMessage = 'Cancel',
    isCancellable = true,
    isLoading,
    align,
    onConfirm,
    onClose,
    disableBackdropClick,
    ...props
}) => {
    const handleClose = React.useCallback(() => {
        if (!disableBackdropClick) {
            onClose();
        }
    }, [disableBackdropClick, onClose]);

    return (
        <StyledDialog onClose={handleClose} {...props} className={classes.dialogWrapper}>
            {title && (
                <DialogTitle align={align} variant="h5">
                    {title}
                </DialogTitle>
            )}
            <DialogContent>
                {isString(description) ? (
                    <DialogContentText variant="body1" align={align} color="inherit">
                        {description}
                    </DialogContentText>
                ) : (
                    description
                )}
            </DialogContent>
            <DialogActions>
                {actionButtons}
                {isCancellable && (
                    <Button color="primary" variant="outlined" onClick={onClose} disabled={isLoading}>
                        {cancelMessage}
                    </Button>
                )}
                <LoadingButton
                    onClick={onConfirm}
                    variant="contained"
                    color="primary"
                    loading={isLoading}
                    disabled={isLoading}
                    fullWidth={!isCancellable && !actionButtons}
                    data-cy="mpo-success-ok"
                >
                    {confirmMessage}
                </LoadingButton>
            </DialogActions>
        </StyledDialog>
    );
};
